/**
 * Site actions renderer
 */
import { Component, ViewEncapsulation } from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import {
  ActionButton,
  ENTITY_TYPES,
  IInventoryCount,
  IModalDialogOptions,
} from '@shared/types';
import { Router } from '@angular/router';
import { get, isEqual } from 'lodash';
import { InventoryCountGroupService, StateService } from '@shared/services';
import { INVENTORY_COUNT_STATUS_VALUE } from '@app/shared/types/enums/inventory-count.enum';
import {
  BTN_CANCEL,
  BTN_DELETE,
  BTN_REMOVE,
} from '@text-content/button-templates';
import { APIActions, UiActions } from '@store/actions';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-inventory-count-actions-renderer',
  templateUrl: 'inventory-count-actions-renderer.component.html',
  styleUrls: ['inventory-count-actions-renderer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XVIInventoryCountRendererComponent implements AgRendererComponent {
  public data: IInventoryCount;
  public actionButtons: ActionButton[];

  // eslint-disable-next-line max-params
  public constructor(
    private readonly router: Router,
    private readonly inventoryCountService: InventoryCountGroupService,
    private readonly stateService: StateService,
    private readonly uiActions: UiActions,
    private readonly apiActions: APIActions,
    private i18nService: XVII18nService,
  ) {}

  public agInit(params: ICellRendererParams): void {
    this.data = params.node.data;
    this.initActionButtons();
  }

  public emitAction(type: string): void {
    if (type === 'edit') {
      this.edit();
    }

    if (type === 'remove') {
      this.remove();
    }
  }

  public async remove(): Promise<void> {
    const message = this.i18nService.instant(
      I18n.ConfirmMessages.AreYouSureYouWantToRemoveItem,
      {
        removableItemsNames: get(this.data, 'name', ''),
        commonName: this.i18nService.instant(I18n.EntityNames.InventoryCount),
      },
    );

    const modalOptions: IModalDialogOptions = {
      title: this.i18nService.instant(I18n.Common.Confirm),
      text: message,
      buttons: [
        {
          ...BTN_DELETE,
          caption: BTN_REMOVE.caption,
        },
        BTN_CANCEL,
      ],
    };

    const result = await this.uiActions.promptFormModal(modalOptions);

    if (!result.confirmed) {
      return;
    }

    const _id = get(this.data, '_id', null);
    const deletePayload = { is_active: false };
    const options = {
      endpoint: _id,
      suppressSuccessMessage: true,
      skipPickingRequiredFields: true,
    };

    await this.apiActions.updateEntityAsPromised(
      ENTITY_TYPES.INVENTORY_COUNT_GROUP,
      deletePayload,
      options,
    );

    this.refresh({});
  }

  public edit(): void {
    this.router.navigate([
      this.inventoryCountService.config.uiRoute,
      get(this, 'data._id', ''),
    ]);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: any): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };
    const entityType = this.inventoryCountService.config.ENTITY_TYPE;
    const previousRequest = this.stateService.getByPath(
      ['entities', entityType, 'previousRequest'],
      defaultRequestParams,
    );

    this.apiActions.fetchList(
      entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  private initActionButtons(): void {
    const isRemovable: boolean = get(this.data, 'is_removable', false);
    const canRemove = this.inventoryCountService.permissionService.canDelete(
      ENTITY_TYPES.INVENTORY_COUNT,
    );

    const editButtonName: string = isEqual(
      this.data.status,
      INVENTORY_COUNT_STATUS_VALUE.Completed,
    )
      ? I18n.Common.View
      : I18n.Common.Edit;

    this.actionButtons = [
      {
        name: editButtonName,
        type: 'edit',
        class: 'menuitem',
        visible: true,
        permissionCodes: [],
      },
      {
        name: I18n.Common.Remove,
        type: 'remove',
        class: 'menuitem',
        visible: canRemove && isRemovable,
        permissionCodes: [],
      },
    ];
  }
}
